import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { KPICard } from "@/components/fleet/reports/KPICard";
import { Fuel, DollarSign, Droplet, Activity } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

interface FuelLog {
  id: string;
  occurred_at: string;
  liters: number;
  price_unit: number;
  total_value?: number | null;
  fuel_type: string;
  odometer?: number;
  fleet_vehicles?: {
    plate: string;
    make: string;
    model: string;
  };
  fleet_providers?: {
    name: string;
  };
}

interface FuelReportsProps {
  data: FuelLog[];
}

export function FuelReports({ data }: FuelReportsProps) {
  const kpis = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalGasto: 0,
        totalLitros: 0,
        precoMedio: 0,
        abastecimentos: 0
      };
    }

    const totalGasto = data.reduce((sum, log) => {
      if (log.total_value) return sum + log.total_value;
      return sum + (log.liters * log.price_unit);
    }, 0);
    const totalLitros = data.reduce((sum, log) => sum + log.liters, 0);
    const precoMedio = data.reduce((sum, log) => sum + log.price_unit, 0) / data.length;

    return {
      totalGasto,
      totalLitros,
      precoMedio,
      abastecimentos: data.length
    };
  }, [data]);

  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Agrupar por mês
    const grouped = data.reduce((acc, log) => {
      const month = new Date(log.occurred_at).toLocaleDateString('pt-BR', { year: 'numeric', month: 'short' });
      if (!acc[month]) {
        acc[month] = { month, gasto: 0, litros: 0 };
      }
      const logGasto = log.total_value || (log.liters * log.price_unit);
      acc[month].gasto += logGasto;
      acc[month].litros += log.liters;
      return acc;
    }, {} as Record<string, { month: string; gasto: number; litros: number }>);

    return Object.values(grouped).sort((a, b) => {
      const dateA = new Date(a.month);
      const dateB = new Date(b.month);
      return dateA.getTime() - dateB.getTime();
    });
  }, [data]);

  const vehicleData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const grouped = data.reduce((acc, log) => {
      const vehicle = log.fleet_vehicles?.plate || 'Sem Placa';
      if (!acc[vehicle]) {
        acc[vehicle] = { vehicle, gasto: 0 };
      }
      const logGasto = log.total_value || (log.liters * log.price_unit);
      acc[vehicle].gasto += logGasto;
      return acc;
    }, {} as Record<string, { vehicle: string; gasto: number }>);

    return Object.values(grouped)
      .sort((a, b) => b.gasto - a.gasto)
      .slice(0, 10);
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Nenhum abastecimento encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total Gasto"
          value={kpis.totalGasto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          icon={DollarSign}
          description="Custo total de abastecimentos"
        />
        <KPICard
          title="Total Litros"
          value={kpis.totalLitros.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) + ' L'}
          icon={Droplet}
          description="Volume total abastecido"
        />
        <KPICard
          title="Preço Médio/Litro"
          value={kpis.precoMedio.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          icon={Activity}
          description="Média do preço por litro"
        />
        <KPICard
          title="Abastecimentos"
          value={kpis.abastecimentos.toString()}
          icon={Fuel}
          description="Total de abastecimentos"
        />
      </div>

      {/* Gráficos */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Evolução de Gastos</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                />
                <Legend />
                <Line type="monotone" dataKey="gasto" name="Gasto" stroke="hsl(var(--primary))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top 10 Veículos por Gasto</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={vehicleData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="vehicle" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                />
                <Legend />
                <Bar dataKey="gasto" name="Gasto" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Abastecimentos</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Data</TableHead>
                <TableHead className="text-xs">Veículo</TableHead>
                <TableHead className="text-xs">Posto</TableHead>
                <TableHead className="text-xs">Combustível</TableHead>
                <TableHead className="text-xs text-right">Litros</TableHead>
                <TableHead className="text-xs text-right">Preço/L</TableHead>
                <TableHead className="text-xs text-right">Total</TableHead>
                <TableHead className="text-xs text-right">Odômetro</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="whitespace-nowrap">
                    {new Date(log.occurred_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell className="font-mono">
                    {log.fleet_vehicles?.plate || 'N/A'}
                  </TableCell>
                  <TableCell>{log.fleet_providers?.name || 'Sem posto'}</TableCell>
                  <TableCell className="capitalize">
                    {log.fuel_type === 'gasoline' ? 'Gasolina' :
                     log.fuel_type === 'ethanol' ? 'Etanol' :
                     log.fuel_type === 'diesel' ? 'Diesel' :
                     log.fuel_type === 'gnv' ? 'GNV' : log.fuel_type}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {log.liters.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {log.price_unit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </TableCell>
                  <TableCell className="text-right font-medium whitespace-nowrap">
                    {(log.total_value || (log.liters * log.price_unit)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {log.odometer?.toLocaleString('pt-BR') || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
